import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from '@heroui/react';
import { FormDrawer } from '@/components/formDrawer';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { RowDrawerBarcodes } from './RowDrawerBarcodes';
import { RowDrawerDiscardDialog } from './RowDrawerDiscardDialog';
import { RowDrawerFields } from './RowDrawerFields';
import { RowDrawerFooter } from './RowDrawerFooter';
import { RowDrawerMeta } from './RowDrawerMeta';
import { RowDrawerNotes } from './RowDrawerNotes';
import { RowDrawerPick } from './RowDrawerPick';
import { RowDrawerResolution } from './RowDrawerResolution';
import type { UseRowDrawerResult } from './useRowDrawer';

type RowDrawerViewProps = UseRowDrawerResult;

/**
 * The row drawer: the app's `FormDrawer` (560 px, full screen below `sm`).
 *
 * - **Header:** "Row 77", what saving does, then the row's status chip and
 *   its SKU with the group's size (`RowDrawerMeta`).
 * - **Body:** the problem card (`RowDrawerNotes`), with what resolves the
 *   row's problems under its sentences (`RowDrawerResolution`); the picked variant
 *   (`RowDrawerPick`); the variant's main and extra barcodes with Make main
 *   (`RowDrawerBarcodes`); a warning when the catalog lists did not load;
 *   and the three groups of fields (`RowDrawerFields`).
 * - **Footer** (`RowDrawerFooter`): Exclude row / Include row, Cancel and
 *   **Save and re-check**.
 * - **Discard question** (`RowDrawerDiscardDialog`): over the drawer while
 *   a dismissal would lose unsaved edits.
 *
 * Nothing renders before a row was opened; while closing, the last row stays.
 */
export const RowDrawerView: React.FC<RowDrawerViewProps> = ({
    isOpen,
    model,
    catalogStatus,
    onRetryCatalog,
    canSave,
    onFieldChange,
    onAxisChange,
    onSave,
    onClose,
    isDiscarding,
    onKeepEditing,
    onDiscard,
    onToggleExclusion,
    onPickCandidate,
    onClearPick,
    onTakeGroupValues,
    onPickVariantValue,
    onApplyVariantValue,
    onApplyBarcodeFix,
    onMakeMainBarcode,
}) => {
    const { t } = useTranslation('pages');
    if (model === null) {
        return null;
    }

    return (
        <>
            <FormDrawer
                footer={
                    <RowDrawerFooter
                        canSave={canSave}
                        isExcluded={model.isExcluded}
                        onClose={onClose}
                        onSave={onSave}
                        onToggleExclusion={onToggleExclusion}
                    />
                }
                isOpen={isOpen}
                meta={<RowDrawerMeta model={model} />}
                onClose={onClose}
                subtitle={t('catalogImport.products.drawer.subtitle')}
                testId="catalog-import-row-drawer"
                title={t('catalogImport.products.drawer.title', {
                    row: model.rowNumber,
                })}
            >
                <RowDrawerNotes
                    isExcluded={model.isExcluded}
                    notes={model.notes}
                    resolution={
                        <RowDrawerResolution
                            model={model}
                            onApplyBarcodeFix={onApplyBarcodeFix}
                            onApplyVariantValue={onApplyVariantValue}
                            onPickCandidate={onPickCandidate}
                            onPickVariantValue={onPickVariantValue}
                            onTakeGroupValues={onTakeGroupValues}
                        />
                    }
                />
                {model.pick === null ? null : (
                    <RowDrawerPick onClear={onClearPick} pick={model.pick} />
                )}
                {model.barcodes === null ? null : (
                    <RowDrawerBarcodes
                        barcodes={model.barcodes}
                        onMakeMain={onMakeMainBarcode}
                    />
                )}
                {catalogStatus === 'error' ? (
                    <Alert
                        status="warning"
                        {...getNodeTestIdProp(
                            'catalog-import-row-drawer-catalog-alert',
                        )}
                    >
                        <Alert.Indicator />
                        <Alert.Content>
                            <Alert.Title>
                                {t(
                                    'catalogImport.products.drawer.catalogFailed',
                                )}
                            </Alert.Title>
                        </Alert.Content>
                        <Button
                            onPress={onRetryCatalog}
                            size="sm"
                            variant="tertiary"
                        >
                            {t('catalogImport.products.drawer.retry')}
                        </Button>
                    </Alert>
                ) : null}
                <RowDrawerFields
                    catalogStatus={catalogStatus}
                    model={model}
                    onAxisChange={onAxisChange}
                    onFieldChange={onFieldChange}
                />
            </FormDrawer>
            <RowDrawerDiscardDialog
                isOpen={isDiscarding}
                onDiscard={onDiscard}
                onKeepEditing={onKeepEditing}
                rowNumber={model.rowNumber}
            />
        </>
    );
};
